<script setup lang="ts">
import { format } from "date-fns";

import {
    NeurologicalCondition, NeurologicalSubCondition, PersonData, PrescribedConditionTypes, MedicalCondition,
} from "@/entities/conditions";
import { Suggestion } from "@/entities/suggestion";
import MedicalFormBuilder from "@/components/medicalConditionForms/MedicalFormBuilder.vue";
import { parseSchema } from "@/components/medicalConditionForms/schema";
import { DatePicker, Question } from "@/components/fields";
import MedicalConditionSearch from "@/components/medicalConditionForms/MedicalConditionSearch.vue";
import { EditableList } from "@/components/EditableList";
import validateNeurologicalForm from "./validators";
import { defaultDateFormat } from "@/constants/date";
import { useMedicalFormWithSubConditions } from "../useMedicalForm";

import neurological from "./neurological.json";

export interface Props {
    condition: NeurologicalCondition,
    personDetails: PersonData,
    subConditionSuggestion: Suggestion | null
}

const props = defineProps<Props>();

const emit = defineEmits<{
        (on:"update:condition", value: NeurologicalCondition): void
    }>();

const getInitialNewCondition = (props: Props): NeurologicalSubCondition | null => {
    if (!props.condition.subConditions?.length) {
        return {
            syntheticId: Date.now(),
            id: Number(props.condition.id) ?? 0,
            type: PrescribedConditionTypes.Neurological,
            name: props.condition.name,
            diagnosisdate: "",
            code: props.condition.code ?? null,
        };
    } else if (props.subConditionSuggestion) {
        return {
            syntheticId: Date.now(),
            ...props.subConditionSuggestion,
            id: Number(props.subConditionSuggestion.id) ?? 0,
            type: PrescribedConditionTypes.Neurological,
            diagnosisdate: "",
            code: props.subConditionSuggestion.code ?? null,
        };
    } else {
        return null;
    }
};

const {
    onConditionUpdate,
    minSubConditionDate,
    state,
    handleUpdate,
    handleUpdateItem,
    handleSave,
} = useMedicalFormWithSubConditions<
    {type: PrescribedConditionTypes.Neurological} & NeurologicalCondition, NeurologicalSubCondition
>({
    condition: {
        ...props.condition,
        type: PrescribedConditionTypes.Neurological,
        subConditions: props.condition.subConditions || [],
        medications: props.condition.medications || [],
    },
    newCondition: getInitialNewCondition(props),
}, emit);

const validators = validateNeurologicalForm();

const handleConditionSelect = (condition: MedicalCondition) => {
    state.newCondition = {
        ...condition,
        type: PrescribedConditionTypes.Neurological,
        diagnosisdate: "",
        syntheticId: Date.now(),
    };
};

const currentDate = new Date();
const maxDate = currentDate;
const minDate = new Date(props.personDetails.dob);
</script>

<template>
    <p class="font-weight-bold-col">
        Declared neurological conditions
    </p>
    <EditableList
        :current-item="state.newCondition"
        :items="condition.subConditions || []"
        :header="state.newCondition?.name"
        @update:items="handleUpdate"
        @update:item="handleUpdateItem"
        @save="handleSave"
    >
        <template
            v-for="item in condition.subConditions"
            #[item.syntheticId]
            :key="item.syntheticId"
        >
            <span
                class="col-md-2 "
            >
                <b>{{ item.name }}</b>
            </span>
        </template>
        <div
            v-if="state.newCondition !== null"
        >
            <p class="font-weight-bold-col">
                {{ state.newCondition.name }}
            </p>
            <Question
                name="diagnosis"
                label="When were you diagnosed?"
                tooltip="Please enter the date the condition was first diagnosed. If you don't know the month, please enter June as a default value."
            >
                <DatePicker
                    :key="state.newCondition.id"
                    v-model="state.newCondition.diagnosisdate"
                    :max-date="maxDate"
                    :min-date="minDate"
                    :show-day-field="false"
                    :validation="validators.diagnosisDate"
                />
            </Question>
        </div>
    </EditableList>
    <MedicalConditionSearch
        id="neurological-condition"
        :key="condition.subConditions?.length"
        label="Add another neurological condition"
        :type="PrescribedConditionTypes.Neurological"
        :disable-add-not-found="true"
        @select:condition="handleConditionSelect"
    />
    <MedicalFormBuilder
        v-if="minSubConditionDate !== null"
        :schema="parseSchema(neurological)"
        :initial-form="state.condition"
        :diagnosis-date="format(minSubConditionDate, defaultDateFormat)"
        @update:form="onConditionUpdate"
    />
</template>

<style>

</style>
